/**
 * ChatToolCards — typed preview cards for tool calls and tool results in the
 * AI chat. Tool calls render as a compact chip row ("⚙ get_time_off · days: 14")
 * with an expandable input JSON; tool results render as a preview card with a
 * per-family icon + one-line human summary, raw JSON behind an expander,
 * charts full-width above, and CSV exports as a Download card.
 */
import { useState } from 'react';
import { Box, Typography, Collapse, Chip, Button, Paper, CircularProgress } from '@mui/material';
import {
  ExpandMore, Build, Download, CalendarMonth, RequestQuote, Feedback as FeedbackIcon,
  Category as CategoryIcon, Storage, Description, PhotoLibrary, ShowChart, HelpOutline,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../services/chatService';
import { InlineChart, parsePlotData } from './InlineChart';
import { parseToolCall, summarizeToolResult, prettifyToolName, type ToolFamily } from './chatToolPreview';
import { openAuthenticatedApiFile } from './chatMessageUtils';

const FAMILY_ICONS: Record<ToolFamily, React.ReactNode> = {
  schedule: <CalendarMonth sx={{ fontSize: 15 }} />,
  export: <Download sx={{ fontSize: 15 }} />,
  quote: <RequestQuote sx={{ fontSize: 15 }} />,
  feedback: <FeedbackIcon sx={{ fontSize: 15 }} />,
  catalog: <CategoryIcon sx={{ fontSize: 15 }} />,
  query: <Storage sx={{ fontSize: 15 }} />,
  report: <Description sx={{ fontSize: 15 }} />,
  photo: <PhotoLibrary sx={{ fontSize: 15 }} />,
  chart: <ShowChart sx={{ fontSize: 15 }} />,
  help: <HelpOutline sx={{ fontSize: 15 }} />,
  generic: <Build sx={{ fontSize: 15 }} />,
};

/** Compact chip-style row for an assistant tool-use message (B4). */
export function ToolCallMessage({ message }: { message: ChatMessage }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const call = parseToolCall(message.ToolInput);

  return (
    <Box sx={{ mb: 0.75, px: 2, display: 'flex', justifyContent: 'flex-start' }}>
      <Box sx={{ maxWidth: '85%', ml: 5.5, minWidth: 0 }}>
        {message.Content && (
          <Box sx={{ mb: 0.5, '& p': { m: 0, fontSize: '0.85rem', lineHeight: 1.6 } }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.Content}</ReactMarkdown>
          </Box>
        )}
        <Box onClick={() => setOpen((o) => !o)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
          px: 1.25, py: 0.4, borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.04) },
        }}>
          <Build sx={{ fontSize: 13, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', fontFamily: "'Fira Code', monospace" }}>
            {call.name}
          </Typography>
          {call.argSummary && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', opacity: 0.8 }} noWrap>
              · {call.argSummary}
            </Typography>
          )}
          <ExpandMore sx={{
            fontSize: 15, color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
          }} />
        </Box>
        <Collapse in={open}>
          <Box component="pre" sx={{
            fontSize: '0.72rem', m: 0, mt: 0.5, p: 1, borderRadius: 1,
            backgroundColor: theme.palette.grey[100], overflow: 'auto', maxHeight: 200,
          }}>
            {JSON.stringify(call.input, null, 2)}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

/** Preview card for tool results — chart full-width, download card, raw JSON expander (B4). */
export function ToolResultMessage({ message }: { message: ChatMessage }) {
  const theme = useTheme();
  const [rawOpen, setRawOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const output = message.ToolOutput || message.Content || '';
  const plotData = parsePlotData(output);
  // Tool-result rows persist {name, input} of THEIR OWN call in ToolInput.
  const toolName = parseToolCall(message.ToolInput).name;
  const preview = summarizeToolResult(toolName === 'tool' ? '' : toolName, output);

  return (
    <Box sx={{ mb: 1, px: 2, display: 'flex', justifyContent: 'flex-start' }}>
      <Box sx={{ maxWidth: '85%', ml: 5.5, width: plotData ? '85%' : undefined, minWidth: 0 }}>
        {/* Chart renders full-width above the preview card */}
        {plotData && <InlineChart data={plotData} />}

        {/* Download card for CSV exports */}
        {preview.download && (
          <Paper variant="outlined" sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, mb: 0.75,
            borderRadius: 2, borderColor: alpha(theme.palette.primary.main, 0.35),
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, flexShrink: 0,
            }}>
              <Download sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap>
                {preview.download.filename}
              </Typography>
              {preview.download.rowCount !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                  {preview.download.rowCount.toLocaleString()} row{preview.download.rowCount === 1 ? '' : 's'} · CSV
                </Typography>
              )}
            </Box>
            <Button size="small" variant="contained" disabled={downloading}
              startIcon={downloading
                ? <CircularProgress size={13} color="inherit" />
                : <Download sx={{ fontSize: 14 }} />}
              onClick={async () => {
                setDownloadError(false);
                setDownloading(true);
                try {
                  await openAuthenticatedApiFile(preview.download!.url);
                } catch {
                  setDownloadError(true);
                } finally {
                  setDownloading(false);
                }
              }}
              sx={{ fontSize: '0.7rem', textTransform: 'none', flexShrink: 0 }}>
              {downloadError ? 'Retry' : downloading ? 'Preparing…' : 'Download'}
            </Button>
          </Paper>
        )}

        {/* Preview card: family icon + human summary + raw JSON expander */}
        <Box sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.default, 0.45), overflow: 'hidden',
        }}>
          <Box onClick={() => setRawOpen((o) => !o)} sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.6, cursor: 'pointer',
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
              {FAMILY_ICONS[preview.family]}
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.74rem', color: 'text.secondary', fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
              {preview.summary}
            </Typography>
            {toolName !== 'tool' && (
              <Chip label={prettifyToolName(toolName)} size="small" variant="outlined"
                sx={{ height: 18, fontSize: '0.62rem', color: 'text.secondary', flexShrink: 0 }} />
            )}
            <ExpandMore sx={{
              fontSize: 16, color: 'text.secondary', flexShrink: 0,
              transform: rawOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
            }} />
          </Box>
          <Collapse in={rawOpen}>
            <Box component="pre" sx={{
              fontSize: '0.72rem', m: 0, mx: 1, mb: 1, p: 1, borderRadius: 1,
              backgroundColor: theme.palette.grey[100], overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap',
            }}>
              {output}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}
